import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import { type VariantProps, cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { sheetBackdropMotion, sheetPopupMotion } from '../lib/motion';
import { Button } from './button';
import { Icon } from './icon';
import { titleVariants } from './title';

/**
 * 오른쪽 가장자리에서 열리는 면 — Base UI `Drawer` 위(D144 ② · ⑨).
 * 초점 가둠 · Esc · 배경막 · 닫기 버튼으로 닫는다. 끌어서 닫는 제스처는 두지 않는다 —
 * 내용 전체가 `data-base-ui-swipe-ignore` 아래에 있다.
 */
export const SheetTrigger = BaseDrawer.Trigger;
export const SheetClose = BaseDrawer.Close;

export const sheetPopupVariants = cva(
  cn(
    'bg-surface-raised shadow-modal flex h-full flex-col rounded-l-2xl outline-none',
    sheetPopupMotion,
  ),
  {
    variants: {
      size: {
        md: 'w-[min(92vw,28rem)]',
        lg: 'w-[min(92vw,40rem)]',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface SheetContentProps
  extends
    Omit<ComponentProps<typeof BaseDrawer.Popup>, 'title'>,
    VariantProps<typeof sheetPopupVariants> {
  title: ReactNode;
  description?: ReactNode;
  /** @default '닫기' */
  closeLabel?: string;
}

/** Portal + 배경막 + 오른쪽 면 + 제목·닫기를 한 번에 감싼다. */
export function SheetContent({
  title,
  description,
  closeLabel = '닫기',
  size,
  className,
  children,
  ...props
}: SheetContentProps) {
  return (
    <BaseDrawer.Portal>
      <BaseDrawer.Backdrop
        className={cn(
          'bg-overlay z-overlay backdrop-blur-scrim fixed inset-0',
          sheetBackdropMotion,
        )}
      />
      <BaseDrawer.Viewport className="z-modal fixed inset-0 flex justify-end">
        <BaseDrawer.Popup className={cn(sheetPopupVariants({ size }), className)} {...props}>
          <BaseDrawer.Content
            data-base-ui-swipe-ignore=""
            className="p-panel gap-card flex min-h-0 flex-1 flex-col overflow-y-auto"
          >
            <div className="gap-card flex items-start justify-between">
              <div className="gap-tight flex min-w-0 flex-col">
                <BaseDrawer.Title className={titleVariants({ level: 'card' })}>
                  {title}
                </BaseDrawer.Title>
                {description !== undefined ? (
                  <BaseDrawer.Description className="text-ink-muted text-sm">
                    {description}
                  </BaseDrawer.Description>
                ) : null}
              </div>
              <BaseDrawer.Close
                render={
                  <Button variant="ghost" size="icon" aria-label={closeLabel} className="-m-snug">
                    <Icon as={X} size="md" />
                  </Button>
                }
              />
            </div>
            {children}
          </BaseDrawer.Content>
        </BaseDrawer.Popup>
      </BaseDrawer.Viewport>
    </BaseDrawer.Portal>
  );
}

/** 오른쪽으로 연다 — 방향은 고정이다. */
export function Sheet(props: Omit<ComponentProps<typeof BaseDrawer.Root>, 'swipeDirection'>) {
  return <BaseDrawer.Root swipeDirection="right" {...props} />;
}

Sheet.Trigger = SheetTrigger;
Sheet.Close = SheetClose;
Sheet.Content = SheetContent;
